<script setup>
/* global axios */
import { onMounted, computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { format, parseISO } from 'date-fns';
import PatientProfilesAPI from 'dashboard/api/patientProfiles.js';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

const PER_PAGE = 50;
const DEBOUNCE_DELAY = 300;

// journey_phase -> rótulo PT-BR (mesma fonte do ClinicalDataSection).
const PHASE_LABELS = {
  pre_op: 'Pré-op',
  surgery: 'Cirurgia realizada',
  post_op_phase_1: 'Pós-op Fase 1',
  post_op_phase_2: 'Pós-op Fase 2',
  discharged: 'Alta',
};

// Opções do dropdown de status (na ordem da jornada).
const PHASE_OPTIONS = [
  { value: 'pre_op', label: 'Pré-op' },
  { value: 'surgery', label: 'Cirurgia realizada' },
  { value: 'post_op_phase_1', label: 'Pós-op Fase 1' },
  { value: 'post_op_phase_2', label: 'Pós-op Fase 2' },
  { value: 'discharged', label: 'Alta' },
];

// risk_level -> rótulo + classe (tokens de tema, sem hex cru).
const RISK_BADGES = {
  verde: { label: '🟢 Verde', class: 'risk-verde' },
  amarelo: { label: '🟡 Amarelo', class: 'risk-amarelo' },
  vermelho: { label: '🔴 Vermelho', class: 'risk-vermelho' },
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// "Ativos" = rota active (não-altas); senão "Todos" (todas as fases).
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');

// ── Estado da lista de patient_profiles ──
const patients = ref([]);
const meta = ref({
  count: 0,
  active_count: 0,
  in_post_op_count: 0,
  discharged_count: 0,
});
const isFetchingList = ref(false);
const currentPage = ref(1);
const physicians = ref([]);

// ── Filtros ──
const searchValue = ref(route.query?.search || '');
const phaseFilter = ref('');
const physicianFilter = ref('');

const hasPatients = computed(() => patients.value.length > 0);
const totalItems = computed(() => meta.value?.count || 0);
const hasMore = computed(() => patients.value.length === PER_PAGE);

// Quando na sub-aba "Ativos", restringe o backend às fases não-alta.
// "Todos" usa o filtro de Status escolhido na página (ou nenhum).
const phaseParam = computed(() => {
  if (isActiveView.value) {
    // active = todas menos discharged; backend não tem param "active",
    // então só aplicamos o filtro de fase se o usuário escolher uma específica.
    return phaseFilter.value && phaseFilter.value !== 'discharged'
      ? phaseFilter.value
      : '';
  }
  return phaseFilter.value || '';
});

function buildAccountUrl(path) {
  const match = window.location.pathname.match(/accounts\/(\d+)/);
  const id = match ? match[1] : 1;
  return `/api/v1/accounts/${id}${path}`;
}

const loadPhysicians = async () => {
  try {
    const { data } = await axios.get(buildAccountUrl('/agents'));
    physicians.value = Array.isArray(data) ? data : data?.payload || [];
  } catch (e) {
    physicians.value = [];
  }
};

const fetchPatients = async (page = 1) => {
  isFetchingList.value = true;
  try {
    const { data } = await PatientProfilesAPI.list({
      page,
      perPage: PER_PAGE,
      phase: phaseParam.value || undefined,
      physicianId: physicianFilter.value || undefined,
      q: searchValue.value || undefined,
    });
    let rows = data.data || [];
    // Na sub-aba "Ativos" sem filtro de fase, escondemos as altas no cliente
    // (o backend não expõe um param "active" no index).
    if (isActiveView.value && !phaseFilter.value) {
      rows = rows.filter(p => p.journey_phase !== 'discharged');
    }
    patients.value = rows;
    meta.value = data.meta || meta.value;
    currentPage.value = page;
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE'));
  } finally {
    isFetchingList.value = false;
  }
};

const updatePageParam = (page, search = '') => {
  const query = { ...route.query, page: page.toString() };
  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }
  router.replace({ query });
};

const goToPage = async page => {
  if (page < 1) return;
  updatePageParam(page, searchValue.value);
  await fetchPatients(page);
};

const onSearchInput = debounce(async value => {
  searchValue.value = value;
  await goToPage(1);
}, DEBOUNCE_DELAY);

const onFilterChange = async () => {
  await goToPage(1);
};

const clearFilters = async () => {
  searchValue.value = '';
  phaseFilter.value = '';
  physicianFilter.value = '';
  await goToPage(1);
};

// ── Labels / badges ──
const phaseLabel = phase => PHASE_LABELS[phase] || '—';
const riskBadge = level => RISK_BADGES[level] || RISK_BADGES.verde;

const formatCreatedAt = createdAt => {
  if (!createdAt) return '';
  // O index serializa created_at como ISO8601.
  try {
    return format(parseISO(createdAt), 'dd/MM/yyyy');
  } catch (e) {
    return '';
  }
};

const goToPatient = contactId => {
  router.push({
    name: 'contacts_edit',
    params: { contactId },
    query: route.query,
  });
};

// ── Exclusão do perfil clínico (não do contato) ──
const singleDeleteDialogRef = ref(null);
const patientToDelete = ref(null);
const isSingleDeleting = ref(false);

const openSingleDeleteDialog = patient => {
  patientToDelete.value = patient;
  singleDeleteDialogRef.value?.open?.();
};

const deleteSinglePatient = async () => {
  const id = patientToDelete.value?.id;
  if (!id) return;

  isSingleDeleting.value = true;
  try {
    await PatientProfilesAPI.delete(id);
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.SUCCESS_MESSAGE'));
    singleDeleteDialogRef.value?.close?.();
    patientToDelete.value = null;
    await fetchPatients(currentPage.value);
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE'));
  } finally {
    isSingleDeleting.value = false;
  }
};

// ── Criação de novo paciente (reusa CreateNewContactDialog) ──
const createNewContactDialogRef = ref(null);

const openNewPatientDialog = () => {
  createNewContactDialogRef.value?.dialogRef.open();
};

const onNewPatientCreated = async contact => {
  try {
    const createdContact = await store.dispatch('contacts/create', contact);
    // O dialog cria o patient_profile vinculado (se houver dados clínicos).
    await createNewContactDialogRef.value?.onSuccess(createdContact);
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
    // Recarrega a lista de pacientes na primeira página.
    await goToPage(1);
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

// Reage à troca de sub-aba (Todos / Ativos) na sidebar.
watch(isActiveView, () => {
  goToPage(1);
});

onMounted(async () => {
  await Promise.all([
    loadPhysicians(),
    fetchPatients(Number(route.query?.page) || 1),
  ]);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles, vue/html-closing-bracket-newline -->
<template>
  <div class="patients-wrap">
    <div
      style="
        max-width: 1280px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 24px;
      "
    >
      <!-- HERO — exact preview structure -->
      <div class="patients-hero">
        <div class="patients-hero-inner">
          <div class="patients-hero-top">
            <div>
              <p class="patients-hero-overline">Pacientes</p>
              <h2>Central de Pacientes</h2>
              <p class="hero-desc">
                Acompanhe cadastros, status assistencial e resumos inteligentes
                em um painel unificado.
              </p>
            </div>
            <div class="patients-hero-actions">
              <button class="btn-hero-primary" @click="openNewPatientDialog">
                + Novo paciente
              </button>
              <button class="btn-hero-secondary">Ver acompanhamento IA</button>
            </div>
          </div>
          <div class="patients-stats">
            <div class="patients-stat-card">
              <div class="stat-card-top">
                <div>
                  <p class="stat-card-label">Total</p>
                  <p class="stat-card-value">{{ meta.count || 0 }}</p>
                </div>
                <span class="stat-card-icon"
                  ><svg
                    style="width: 20px; height: 20px"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path
                      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                    /></svg
                ></span>
              </div>
            </div>
            <div class="patients-stat-card">
              <div class="stat-card-top">
                <div>
                  <p class="stat-card-label">Ativos</p>
                  <p class="stat-card-value">{{ meta.active_count || 0 }}</p>
                </div>
                <span class="stat-card-icon"
                  ><svg
                    style="width: 20px; height: 20px"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg
                ></span>
              </div>
            </div>
            <div class="patients-stat-card">
              <div class="stat-card-top">
                <div>
                  <p class="stat-card-label">Em pós-op</p>
                  <p class="stat-card-value">
                    {{ meta.in_post_op_count || 0 }}
                  </p>
                </div>
                <span class="stat-card-icon"
                  ><svg
                    style="width: 20px; height: 20px"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"
                    /></svg
                ></span>
              </div>
            </div>
            <div class="patients-stat-card">
              <div class="stat-card-top">
                <div>
                  <p class="stat-card-label">Alta</p>
                  <p class="stat-card-value">
                    {{ meta.discharged_count || 0 }}
                  </p>
                </div>
                <span class="stat-card-icon"
                  ><svg
                    style="width: 20px; height: 20px"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 6 9 17l-5-5" /></svg
                ></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- PILL TABS — exact preview -->
      <div class="patients-pill-tabs">
        <button class="patients-pill-tab active">Lista de Pacientes</button>
        <button class="patients-pill-tab">Acompanhamento por IA</button>
      </div>

      <!-- FILTER CARD -->
      <div class="filter-card">
        <div class="filter-grid">
          <div>
            <label class="filter-label">Buscar</label>
            <input
              class="filter-input"
              placeholder="Nome ou telefone..."
              :value="searchValue"
              @input="onSearchInput($event.target.value)"
            />
          </div>
          <div>
            <label class="filter-label">Status</label>
            <select
              v-model="phaseFilter"
              class="filter-select"
              @change="onFilterChange"
            >
              <option value="">Todas</option>
              <option
                v-for="opt in PHASE_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div>
            <label class="filter-label">Médico</label>
            <select
              v-model="physicianFilter"
              class="filter-select"
              @change="onFilterChange"
            >
              <option value="">Todos</option>
              <option v-for="u in physicians" :key="u.id" :value="u.id">
                {{ u.name }}
              </option>
            </select>
          </div>
          <button class="btn-clear" @click="clearFilters">
            Limpar filtros
          </button>
        </div>
      </div>

      <!-- LOADING -->
      <div
        v-if="isFetchingList"
        style="
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
        "
      >
        <Spinner />
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="!hasPatients" class="empty-state">
        <div class="empty-state-icon">
          <svg
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            viewBox="0 0 24 24"
            style="width: 56px; height: 56px"
          >
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <h3 class="empty-state-title">Nenhum paciente encontrado</h3>
        <p class="empty-state-desc">Cadastre um novo paciente para começar.</p>
      </div>

      <!-- DATA TABLE — patient_profiles -->
      <div v-else class="data-table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 22%">Nome</th>
              <th style="width: 14%">Telefone</th>
              <th style="width: 15%">Status</th>
              <th style="width: 12%">Prioridade</th>
              <th style="width: 14%">Médico</th>
              <th style="width: 9%">Alertas</th>
              <th style="width: 14%; text-align: right">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="patient in patients"
              :key="patient.id"
              @click="goToPatient(patient.contact_id)"
            >
              <td>
                <div class="name">{{ patient.name || '—' }}</div>
                <div
                  v-if="patient.created_at"
                  class="created-at text-n-slate-11"
                >
                  Criado em {{ formatCreatedAt(patient.created_at) }}
                </div>
              </td>
              <td>{{ patient.phone_number || '—' }}</td>
              <td>
                <span class="status-badge">{{
                  phaseLabel(patient.journey_phase)
                }}</span>
              </td>
              <td>
                <span
                  class="risk-badge"
                  :class="riskBadge(patient.risk_level).class"
                >
                  {{ riskBadge(patient.risk_level).label }}
                </span>
              </td>
              <td>{{ patient.physician?.name || '—' }}</td>
              <td>
                <span v-if="patient.open_alerts_count > 0" class="alerts-badge">
                  {{ patient.open_alerts_count }}
                </span>
                <span v-else class="text-n-slate-11">—</span>
              </td>
              <td style="text-align: right">
                <div style="display: flex; gap: 6px; justify-content: flex-end">
                  <button
                    class="btn-edit"
                    @click.stop="goToPatient(patient.contact_id)"
                  >
                    Editar
                  </button>
                  <button
                    class="btn-delete"
                    title="Excluir paciente"
                    aria-label="Excluir paciente"
                    @click.stop="openSingleDeleteDialog(patient)"
                  >
                    <svg
                      style="width: 14px; height: 14px"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINATION -->
      <div
        v-if="hasPatients"
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
        "
      >
        <span class="pagination-info text-n-slate-11">
          Exibindo {{ patients.length }} de {{ totalItems }} pacientes
        </span>
        <div style="display: flex; align-items: center; gap: 8px">
          <button
            class="btn-page"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >
            &laquo;
          </button>
          <span class="btn-page-current">{{ currentPage }}</span>
          <button
            class="btn-page"
            :disabled="!hasMore"
            @click="goToPage(currentPage + 1)"
          >
            &raquo;
          </button>
        </div>
      </div>
    </div>
    <CreateNewContactDialog
      ref="createNewContactDialogRef"
      @create="onNewPatientCreated"
    />
    <Dialog
      ref="singleDeleteDialogRef"
      type="alert"
      title="Excluir paciente"
      description="Isso remove o cadastro clínico do paciente. O contato e o histórico de conversas são preservados. Deseja continuar?"
      :confirm-button-label="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CONFIRM')"
      :is-loading="isSingleDeleting"
      @confirm="deleteSinglePatient"
    />
  </div>
</template>

<!-- CSS copied VERBATIM from preview-clara.html -->
<style lang="scss">
.patients-wrap {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
}
.dark .patients-wrap {
  background: #111113;
}

.patients-hero {
  overflow: hidden;
  border-radius: 20px;
  background: linear-gradient(to right, #0ea5e9, #2563eb, #4f46e5);
  &,
  & * {
    color: #fff;
  }
  color: #fff;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.dark .patients-hero {
  background: linear-gradient(to right, #0c4a6e, #1e3a8a, #312e81);
}
.patients-hero-inner {
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.patients-hero-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.patients-hero-overline {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: #a5f3fc;
}
.patients-hero h2 {
  font-size: 28px;
  font-weight: 600;
  margin-top: 4px;
}
.patients-hero p.hero-desc {
  font-size: 13px;
  color: #cffafe;
  margin-top: 8px;
  max-width: 600px;
}
.patients-hero-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.btn-hero-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: #1d4ed8;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  cursor: pointer;
}
.btn-hero-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
}
.patients-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 240px));
  gap: 12px;
}
@media (max-width: 900px) {
  .patients-stats {
    grid-template-columns: 1fr;
  }
}
.patients-stat-card {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  padding: 16px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}
.stat-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.stat-card-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #a5f3fc;
}
.stat-card-value {
  font-size: 24px;
  font-weight: 600;
  margin-top: 4px;
}
.stat-card-icon {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px;
  display: flex;
}
.stat-card-icon svg {
  width: 20px;
  height: 20px;
}

.patients-pill-tabs {
  display: inline-flex;
  border-radius: 9999px;
  background: #fff;
  padding: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}
.dark .patients-pill-tabs {
  background: #272a2d;
  border-color: #3a3d41;
}
.patients-pill-tab {
  border-radius: 9999px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  color: #374151;
  border: none;
  cursor: pointer;
}
.dark .patients-pill-tab {
  color: #b0b4ba;
}
.patients-pill-tab.active {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.filter-card {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border: 1px solid #e5e7eb;
}
.dark .filter-card {
  background: rgba(24, 25, 27, 0.8);
  border-color: #3a3d41;
}
.filter-grid {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr auto;
  gap: 16px;
  align-items: end;
}
/* Cada coluna empurra o controle pro rodapé, então campo/dropdown/botão
   ficam na MESMA linha de base, independente da altura do rótulo. */
.filter-grid > div {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
}
.filter-input,
.filter-select {
  margin: 0;
}
@media (max-width: 900px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}
.filter-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 6px;
}
.dark .filter-label {
  color: #8b8d98;
}
.filter-input {
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  background: #f8fafc;
  color: #334155;
}
.dark .filter-input {
  background: #111113;
  border-color: #3a3d41;
  color: #edeef0;
}
.filter-select {
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  background: #fff;
  color: #334155;
  cursor: pointer;
}
.dark .filter-select {
  background: #18191b;
  border-color: #3a3d41;
  color: #edeef0;
}
.btn-clear {
  height: 38px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  white-space: nowrap;
}
.dark .btn-clear {
  background: #272a2d;
  border-color: #3a3d41;
  color: #b0b4ba;
}

.data-table-wrap {
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
}
.dark .data-table-wrap {
  background: #18191b;
  border-color: #272a2d;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th {
  text-align: left;
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  border-bottom: 1px solid #f1f5f9;
  background: #f9fafb;
}
.dark .data-table th {
  color: #8b8d98;
  border-bottom-color: #272a2d;
  background: #1e1f21;
}
.data-table td {
  padding: 14px 16px;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #f8fafc;
}
.dark .data-table td {
  color: #b0b4ba;
  border-bottom-color: #1e1f21;
}
.data-table tbody tr {
  transition: background 0.1s;
  cursor: pointer;
}
.data-table tbody tr:hover {
  background: rgba(59, 130, 246, 0.04);
}
.dark .data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}
.data-table .name {
  font-weight: 600;
  color: #0f172a;
}
.dark .data-table .name {
  color: #edeef0;
}
.data-table .sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
.data-table .created-at {
  font-size: 11px;
  margin-top: 2px;
}

/* STATUS (journey_phase) */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  @apply bg-n-slate-3 text-n-slate-12;
}

/* PRIORIDADE (risk_level) — tokens de tema, sem hex cru */
.risk-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.risk-badge.risk-verde {
  @apply bg-n-teal-3 text-n-teal-11;
}
.risk-badge.risk-amarelo {
  @apply bg-n-amber-3 text-n-amber-11;
}
.risk-badge.risk-vermelho {
  @apply bg-n-ruby-3 text-n-ruby-11;
}

/* ALERTAS (open_alerts_count) */
.alerts-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  @apply bg-n-ruby-9 text-white;
}

.pagination-info {
  font-size: 13px;
}
.btn-edit {
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
}
.dark .btn-edit {
  background: #272a2d;
  border-color: #3a3d41;
  color: #b0b4ba;
}
.btn-delete {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 9999px;
  border: 1px solid #fecaca;
  background: #fff;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
  cursor: pointer;
}
.dark .btn-delete {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 32px;
  text-align: center;
}
.empty-state-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}
.empty-state-icon svg {
  width: 56px;
  height: 56px;
  color: #059669;
}
.dark .empty-state-icon svg {
  color: #34d399;
}
.empty-state-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
}
.empty-state-desc {
  font-size: 13px;
  color: #6b7280;
}
.dark .empty-state-desc {
  color: #8b8d98;
}

.btn-page {
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
.dark .btn-page {
  background: #272a2d;
  border-color: #3a3d41;
  color: #b0b4ba;
}
.btn-page-current {
  padding: 4px 10px;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}
</style>
